import { get, set } from 'lodash';
import { fromZonedTime } from 'date-fns-tz';
import { TimeZone, timeZoneMapping } from './timeZone/enum';
import { DateTime } from 'luxon';
import { validateDate } from './dateFormat/formatDateTime';

import * as Sentry from '@sentry/react';

const luxonDateToUtc = (dateTime: DateTime) => {
  // We have issues with locale timeZone, so we make sure it is UTC
  let isoString = dateTime.toISO();
  if (isoString) {
    isoString = isoString.replace(/([+-]\d{2}:\d{2})$/, '+00:00');
    dateTime = DateTime.fromISO(isoString);
  }

  return dateTime;
};

function parseHumanDateString(dateString: string, timeZone?: string) {
  // Early return for null/undefined/empty strings - no Sentry capture needed
  if (!dateString || dateString.trim() === '') {
    return null;
  }

  // Trim whitespace from the input and normalize internal spaces
  dateString = dateString.trim().replace(/\s+/g, ' ');

  // Early return for strings that don't contain any numbers - likely not date strings
  // This prevents excessive Sentry noise from parsing non-date strings
  if (!/\d/.test(dateString)) {
    return null;
  }

  // Handle numeric timezone offsets like +04, +0400, -05:30, etc.
  const numericTimezoneRegex = /([+-]\d{2}(?::?\d{2})?)\s*$/;
  const numericTimezoneMatch = dateString.match(numericTimezoneRegex);

  if (numericTimezoneMatch) {
    const offset = numericTimezoneMatch[1];
    // Normalize the offset format for ISO string compatibility
    let normalizedOffset = offset;

    // If it's just +04 or -05, add :00
    if (/^[+-]\d{2}$/.test(offset)) {
      normalizedOffset = offset + ':00';
    }
    // If it's +0400 format, add colon
    else if (/^[+-]\d{4}$/.test(offset)) {
      normalizedOffset = offset.slice(0, 3) + ':' + offset.slice(3);
    }

    // Remove the timezone from the original string and try to parse as ISO
    const baseDateString = dateString.replace(numericTimezoneRegex, '').trim();

    // Try to create an ISO-compatible string
    if (/^\d{4}-\d{2}-\d{2}/.test(baseDateString)) {
      let isoString = baseDateString;

      // Add time if missing
      if (!/T\d{2}:\d{2}/.test(isoString)) {
        if (/ \d{2}:\d{2}/.test(isoString)) {
          isoString = isoString.replace(' ', 'T');
        } else {
          isoString += 'T00:00:00';
        }
      }

      // Add seconds if missing
      if (!/:\d{2}:\d{2}/.test(isoString)) {
        isoString += ':00';
      }

      // Add the normalized timezone offset
      isoString += normalizedOffset;

      try {
        const date = new Date(isoString);
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (e) {
        // Fall through to other parsing methods
      }
    }
  }

  for (const [key, value] of Object.entries(timeZoneMapping)) {
    // Skip UTC mapping since Luxon understands 'UTC' natively in human-readable formats
    // The 'Z' replacement is only useful for ISO formats, which are handled elsewhere
    if (key === 'UTC') {
      continue;
    }
    if (dateString.includes(key)) {
      dateString = dateString.replace(key, value);
      break;
    }
  }

  // First try to parse as simple ISO date format (YYYY-MM-DD)
  try {
    // Check for simple date format like "2022-07-01"
    const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (simpleDateRegex.test(dateString)) {
      const dateTime = DateTime.fromISO(dateString);
      if (dateTime.isValid) {
        return dateTime.toJSDate();
      }
    }

    // Check for YYYY-MM-DD HH:mm:ss format without timezone
    const datetimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (datetimeRegex.test(dateString)) {
      // Convert to ISO format and parse as UTC
      const isoString = dateString.replace(' ', 'T') + 'Z';
      const date = new Date(isoString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Check for YYYY-MM-DD HH:mm format without timezone
    const datetimeNoSecondsRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (datetimeNoSecondsRegex.test(dateString)) {
      // Convert to ISO format and parse as UTC
      const isoString = dateString.replace(' ', 'T') + ':00Z';
      const date = new Date(isoString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Try to parse the date string with seconds
    let dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss z', {
      setZone: true,
    });

    if (!dateTime.isValid) {
      // If the first format fails, try without seconds
      dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm z', {
        setZone: true,
      });
    }

    if (!dateTime.isValid) {
      if (timeZone) {
        dateString = `${dateString} ${timeZone}`;
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss z', {
          setZone: true,
        });
      } else {
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm:ss');
        dateTime = luxonDateToUtc(dateTime);
      }
    }

    if (!dateTime.isValid) {
      if (timeZone) {
        dateString = `${dateString} ${timeZone}`;
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm z', {
          setZone: true,
        });
      } else {
        // If the first format fails, try without seconds
        dateTime = DateTime.fromFormat(dateString, 'dd MMM yyyy HH:mm');
        dateTime = luxonDateToUtc(dateTime);
      }
    }

    // Check if dateTime is valid before converting to native JavaScript Date object
    if (dateTime.isValid) {
      const jsDate = dateTime.toJSDate();
      return jsDate;
    } else {
      throw new Error(
        `Invalid date format for "${dateString}": ${dateTime.toString()}`
      );
    }
  } catch (error) {
    console.error('Error parsing date string:', error, dateString);

    // Only capture exceptions for strings that look like they could be dates
    // and are not null/undefined (additional safety check)
    if (dateString && dateString.trim() !== '') {
      Sentry.captureException(error, {
        tags: {
          function: 'parseHumanDateString',
          category: 'date_parsing_exception',
        },
        extra: {
          originalDateString: dateString,
          timeZone: timeZone,
        },
        level: 'error',
        fingerprint: ['parseHumanDateString', dateString], // Group similar errors together
      });
    }

    return null;
  }
}

function isISODateString(dateString: string) {
  const isoRegex =
    /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
  return isoRegex.test(dateString);
}

export function parseDate(dateString: string | number, timeZone?: string) {
  // Early return for empty/null/undefined/calculating strings to avoid Sentry noise
  if (
    !dateString ||
    dateString === '' ||
    dateString === null ||
    dateString === undefined ||
    (typeof dateString === 'string' &&
      dateString.toLowerCase().includes('calculating'))
  ) {
    return null;
  }

  // Normalize whitespace for string inputs
  if (typeof dateString === 'string') {
    dateString = dateString.trim().replace(/\s+/g, ' ');
  }

  let date;

  if (typeof dateString === 'number' || /^\d+(\.\d+)?$/.test(dateString)) {
    let timestamp = parseFloat(dateString.toString());
    if (dateString.toString().split('.')[0].length === 10) {
      timestamp *= 1000; // Convert seconds to milliseconds if it's a 10-digit number
    }
    date = new Date(timestamp);
  }
  // Check for ISO 8601 format
  else if (isISODateString(dateString)) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      date = new Date(`${dateString}T00:00:00Z`);
    } else if (dateString.endsWith('Z')) {
      // IMPORTANT: When a date string already has the 'Z' indicator (e.g., "2025-05-02T11:50:39.654210Z"),
      // it explicitly indicates that the time is already in UTC/Zulu time
      // Using fromZonedTime() on a Z-terminated string can cause incorrect double-conversion
      // Instead, we use the native Date constructor which correctly interprets the Z indicator
      date = new Date(dateString);
    } else {
      // IMPORTANT: For ISO strings without timezone information (e.g., "2025-05-02T11:50:39.654210"),
      // we need to explicitly specify that they should be interpreted as UTC
      // Without this, JavaScript would interpret the string as a local time, leading to incorrect timestamps
      // fromZonedTime() correctly converts from the specified timezone (UTC) to UTC
      date = fromZonedTime(dateString, TimeZone.UTC);
    }
  }
  // Check for YYYY-MM-DD HH:mm[:ss] UTC format (e.g., "2025-09-06 08:00 UTC" or "2025-09-15 14:30:45 UTC")
  // Case-insensitive to handle "UTC", "utc", "Utc", etc.
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(?::\d{2})? UTC$/i.test(dateString)) {
    // Remove " UTC" (case insensitive) and replace space with "T", then add "Z" for UTC
    const cleanString = dateString.replace(/ UTC$/i, '').replace(' ', 'T');
    // Add seconds if missing
    const isoString = /:\d{2}:\d{2}$/.test(cleanString)
      ? cleanString + 'Z'
      : cleanString + ':00Z';
    date = new Date(isoString);
  }
  // Check for YYYY-MM-DD UTC format (date only, no time - e.g., "2025-09-15 UTC")
  else if (/^\d{4}-\d{2}-\d{2} UTC$/i.test(dateString)) {
    // Remove " UTC" and add default time
    const cleanString = dateString.replace(/ UTC$/i, '');
    date = new Date(`${cleanString}T00:00:00Z`);
  }
  // Check for YYYY/MM/DD HH:mm[:ss] UTC format (with forward slashes - e.g., "2025/12/03 21:00 UTC" or "2025/12/05 08:57:55 UTC")
  // Case-insensitive to handle "UTC", "utc", "Utc", etc.
  else if (/^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}(?::\d{2})? UTC$/i.test(dateString)) {
    // Remove " UTC" (case insensitive), replace forward slashes with hyphens, and replace space with "T", then add "Z" for UTC
    const cleanString = dateString.replace(/ UTC$/i, '').replace(/\//g, '-').replace(' ', 'T');
    // Add seconds if missing
    const isoString = /:\d{2}:\d{2}$/.test(cleanString)
      ? cleanString + 'Z'
      : cleanString + ':00Z';
    date = new Date(isoString);
  }
  // Check for YYYY/MM/DD UTC format (date only, no time, with forward slashes - e.g., "2025/12/15 UTC")
  else if (/^\d{4}\/\d{2}\/\d{2} UTC$/i.test(dateString)) {
    // Remove " UTC", replace forward slashes with hyphens, and add default time
    const cleanString = dateString.replace(/ UTC$/i, '').replace(/\//g, '-');
    date = new Date(`${cleanString}T00:00:00Z`);
  }
  // Check for DD-MM-YY HH:mm[:ss] [UTC] format (e.g., "27-10-25 10:52" or "27-10-25 10:52:00 UTC")
  // This is a machine-readable format, not human-readable like "27 Oct 2025"
  else if (
    /^\d{2}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?( UTC)?$/i.test(dateString)
  ) {
    // Remove optional " UTC" suffix (case insensitive)
    const cleanString = dateString.replace(/ UTC$/i, '').trim();

    // Determine if we have seconds or not
    const hasSeconds = /:\d{2}:\d{2}$/.test(cleanString);
    const format = hasSeconds ? 'dd-MM-yy HH:mm:ss' : 'dd-MM-yy HH:mm';

    // Replace 'T' with space if present (to handle both separators)
    const normalizedString = cleanString.replace('T', ' ');

    const dateTime = DateTime.fromFormat(normalizedString, format, {
      zone: 'UTC',
    });

    if (dateTime.isValid) {
      date = dateTime.toJSDate();
    } else {
      // If parsing fails, fall through to human-readable format parser
      date = parseHumanDateString(dateString, timeZone);
    }
  }
  // Check for human-readable format
  else {
    date = parseHumanDateString(dateString, timeZone);
  }

  // Validate date object
  if (!validateDate(date)) {
    const error = new Error(
      `Date validation failed for "${dateString}": parsed date is invalid`
    );
    Sentry.captureException(error, {
      tags: {
        function: 'parseDate',
        category: 'date_validation_error',
      },
      extra: {
        originalInput: dateString,
        timeZone: timeZone,
        parsedDate: date,
        dateType: typeof dateString,
      },
      level: 'warning',
      fingerprint: ['parseDate', 'validation_failed', String(dateString)],
    });

    return null;
  }

  return date;
}

export const convertFieldsToDates = (
  input: Record<string, any> | Record<string, any>[],
  fields: string[],
  timeZone?: string
) => {
  const processField = (obj: Record<string, any>, fieldParts: any) => {
    if (fieldParts.length === 0) return;

    const currentPart = fieldParts.shift();
    if (!currentPart) return;

    const value = get(obj, currentPart);

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object') {
          processField(item, [...fieldParts]);
        } else {
          const arrayField = `${currentPart}[${index}]`;
          if (fieldParts.length === 0) {
            const arrayValue = get(obj, arrayField);
            if (arrayValue) {
              set(obj, arrayField, parseDate(arrayValue, timeZone));
            }
          }
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      processField(value, [...fieldParts]);
    } else if (fieldParts.length === 0 && currentPart in obj) {
      set(obj, currentPart, parseDate(value, timeZone));
    }
  };

  if (Array.isArray(input)) {
    input.forEach((object) => convertFieldsToDates(object, fields));
    return;
  }

  const object = input;

  fields.forEach((field) => {
    const fieldParts = field.split('.');
    processField(object, fieldParts);
  });
};
